// TimezoneDriftStrip — the one place the browser's zone is read (ADR-082 §3): when
// it differs from the ACCOUNT's zone — by NAME (the word follows the name, §2:
// Riyadh and Kuwait are one clock under one name, Kolkata and Calcutta one zone
// under two ids — no question there) or by the clock it reads TODAY — one calm
// strip asks which to keep. (ICU names a zone with its country the moment its clock
// parts from its metazone's — "Eastern European Time (Lebanon)" — so two zones that
// read differently never share a name.)
// "Use {browser}" sets the account's zone (the profile patch — every screen,
// email and message follows); "Keep {account}" dismisses this PAIR, in this
// browser, for this person (`localStorage` — a non-travelling display pref,
// ADR-037 §6). A new pair asks again; a changed account zone retires the strip.
//
// First of the session's strips (ProjectStrips: the person's, then the project's)
// — a polite `status`, never an alert; one line like the others; nothing on a
// page depends on its height (ShellTop measures the pinned block).
//
// The zones are NAMED, never shown as ids: `Intl`'s generic name in the viewer's
// language ("Arabian Standard Time" · « heure d’Arabie »), the id only when the
// runtime has no words for it — no city list exists in any language.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { useMe } from '../../hooks/useMe.js';
import { useProfilePrefs } from '../../hooks/useProfilePrefs.js';
import { zoneName, zoneOffsetNow } from '../../lib/format.js';
import { Banner } from '../ui/Banner.js';
import { Button } from '../ui/Button.js';
import { pageBox, pageGutter } from './pageBox.js';

const selectUserId = (m: MeResponse) => m.user.id;
const selectAccountZone = (m: MeResponse) => m.user.timezone_iana;

const storageKey = (userId: string) => `tz-drift:${userId}`;

/** The browser's zone — read ONCE per mount, here and nowhere else. */
function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function readDismissed(userId: string): string | null {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function writeDismissed(userId: string, pair: string): void {
  try {
    localStorage.setItem(storageKey(userId), pair);
  } catch {
    // A private window or blocked storage: the strip simply asks again next time.
  }
}

export function TimezoneDriftStrip({ browser: browserOverride }: { /** Stories only — the browser's zone is otherwise read from the runtime. */ browser?: string } = {}) {
  const { t, i18n } = useTranslation('nav');
  const { data: userId } = useMe(selectUserId);
  const { data: accountZone } = useMe(selectAccountZone);
  const prefs = useProfilePrefs();
  const [browser] = useState(() => browserOverride ?? browserZone());
  const [dismissed, setDismissed] = useState<string | null>(null);

  if (!userId || !accountZone || !browser || browser === accountZone) return null;
  const browserWord = zoneName(browser, i18n.language);
  const accountWord = zoneName(accountZone, i18n.language);
  if (browserWord === accountWord && zoneOffsetNow(browser) === zoneOffsetNow(accountZone)) return null;
  const pair = `${browser}|${accountZone}`;
  if (dismissed === pair || readDismissed(userId) === pair) return null;

  return (
    <div className={pageBox}>
      <Banner variant="neutral" align="center" role="status" className={pageGutter}>
        {/* One sentence that WRAPS on a phone — the pieces flow, never squeeze into columns (the cannot-refresh band's row). */}
        <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <span>{t('timezoneDrift.notice', { browser: browserWord, account: accountWord })}</span>
          <span aria-hidden>·</span>
          {/* The negative margin grows the hit target (WCAG 2.5.8) without growing the strip. */}
          <Button variant="link" className="-my-1 py-1" onClick={() => prefs.setTimezone(browser)}>
            {t('timezoneDrift.useBrowser', { zone: browserWord })}
          </Button>
          <span aria-hidden>·</span>
          <Button
            variant="link"
            className="-my-1 py-1"
            onClick={() => {
              writeDismissed(userId, pair);
              setDismissed(pair);
            }}
          >
            {t('timezoneDrift.keep', { zone: accountWord })}
          </Button>
        </span>
      </Banner>
    </div>
  );
}
